import { isTipDismissed, TipKey } from "@serophin/nottldr-shared";
import { Loader2, Pause, Play } from "lucide-preact";
import { useState } from "preact/hooks";
import { useAudioPlayer } from "../../contexts/AudioPlayerContext";
import { TipModal } from "./TipModal";

export type AudioPlayerButtonProps = {
  authorAlias: string;
  articleId: string;
  articleTitle?: string;
  class?: string;
  iconClass?: string;
  iconSize?: number;
  source?: "card" | "detail";
};

type Playback = {
  isPlaying: boolean;
  isLoading: boolean;
  play: () => Promise<void>;
  handleClick: (event: MouseEvent) => Promise<void>;
  showTip: boolean;
  closeTip: () => void;
};

/** What the button does: starting a track from a card turns the list into a playlist, and the first time the visitor is told. */
const usePlayback = ({ authorAlias, articleId, articleTitle, source }: AudioPlayerButtonProps): Playback => {
  const audio = useAudioPlayer();
  const [showTip, setShowTip] = useState(false);
  const isCurrent = audio.currentArticle?.authorAlias === authorAlias && audio.currentArticle?.articleId === articleId;
  const isPlaying = audio.isArticlePlaying(authorAlias, articleId);
  const play = () => audio.togglePlayPause(authorAlias, articleId, articleTitle);

  const handleClick = async (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (source === "card" && !isPlaying && !isCurrent && !(await isTipDismissed(TipKey.PLAYLIST_MODE))) {
      setShowTip(true);
      return;
    }
    await play();
  };

  return { isPlaying, isLoading: isCurrent && audio.loadingState === "loading", play, handleClick, showTip, closeTip: () => setShowTip(false) };
};

const PlayIcon = ({ isPlaying, isLoading, size, iconClass }: { isPlaying: boolean; isLoading: boolean; size: number; iconClass?: string }) => {
  if (isLoading) {
    return <Loader2 size={size} class={`animate-spin ${iconClass}`} />;
  }
  return isPlaying ? <Pause size={size} class={iconClass} fill="currentColor" /> : <Play size={size} class={iconClass} fill="currentColor" />;
};

/** The play button of an article: it plays, pauses and shows loading; the player itself lives in the audio context. */
export const AudioPlayerButton = (props: AudioPlayerButtonProps) => {
  const { authorAlias, articleId, class: className, iconClass, iconSize = 20 } = props;
  const playback = usePlayback(props);

  return (
    <>
      <button
        type="button"
        id={`btn-play-${authorAlias}.${articleId}`}
        onClick={playback.handleClick}
        title={playback.isLoading ? "Loading..." : "Play Audio"}
        class={`p-2 rounded-full transition-colors duration-200 cursor-pointer ${className || "bg-black/50 hover:bg-black/75 backdrop-blur-sm"}`}
        aria-label={playback.isLoading ? "Loading" : "Play audio"}
        disabled={playback.isLoading}
        data-test-click="play-audio"
        data-test-article={articleId}
        data-test-author={authorAlias}
      >
        <PlayIcon isPlaying={playback.isPlaying} isLoading={playback.isLoading} size={iconSize} iconClass={iconClass} />
      </button>
      <TipModal
        isOpen={playback.showTip}
        tipKey={TipKey.PLAYLIST_MODE}
        title="Playlist Mode"
        message="When you start playing in a list of articles, we'll automatically play the next articles in order - like a playlist. Sit back and enjoy!"
        onConfirm={() => {
          void playback.play();
          playback.closeTip();
        }}
      />
    </>
  );
};
